import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  Stack,
  Avatar,
  CardHeader,
  LinearProgress
} from "@mui/material";
import { 
  Add, 
  Remove, 
  Save, 
  ArrowBack, 
  Search, 
  CheckCircle,
  Inventory,
  LocalHospital,
  MeetingRoom,
  TrendingUp,
  Bed,
  Hotel,
  Description
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { 
  getLinenItems, 
  createLinenIssue,
  getWardsAndFloors,
  getAvailableRooms,
  getLinenIssues,
  getLinenIssueStats
} from "../../services/api";

// Enhanced StatsCard for issue summary
const StatsCard = ({ title, value, subtitle, color = "primary", icon: Icon, ...props }) => (
  <Card 
    {...props}
    sx={{ 
      borderRadius: 3, 
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      ...props.sx 
    }}
  >
    <CardContent sx={{ p: 3, textAlign: 'center' }}>
      <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
        <Icon sx={{ fontSize: 32, color: 'white' }} />
      </Avatar>
      <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
        {value}
      </Typography>
      <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export const LinenIssue = () => {
  const { enqueueSnackbar } = useSnackbar();
  const steps = ["Select Location", "Select Linen Items", "Review & Submit"];
  const [activeStep, setActiveStep] = useState(0);
  const [wards, setWards] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedWard, setSelectedWard] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [linenItems, setLinenItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingWards, setLoadingWards] = useState(false);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [linenRes, wardsRes] = await Promise.all([
        getLinenItems({ status: "active" }),
        getWardsAndFloors()
      ]);
      
      setLinenItems(linenRes.data?.items || []);
      setWards(wardsRes.data?.wards || []);
      
    } catch (err) {
      console.error("Load data error:", err);
      enqueueSnackbar("❌ Failed to load data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Load rooms when ward is selected
  const loadRooms = useCallback(async (wardId) => {
    if (!wardId) {
      setRooms([]);
      return;
    }
    
    try {
      setLoadingWards(true);
      const roomsRes = await getAvailableRooms({ ward: wardId, occupancy: "all" });
      setRooms(roomsRes.data?.rooms || []);
    } catch (err) {
      console.error("Load rooms error:", err);
      enqueueSnackbar("❌ Failed to load rooms", { variant: "error" });
    } finally {
      setLoadingWards(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedWard) {
      loadRooms(selectedWard);
    } else {
      setRooms([]);
      setSelectedRoom("");
    }
  }, [selectedWard, loadRooms]);

  const addItem = (item) => {
    const exists = selectedItems.find((i) => i.item._id === item._id);
    if (exists) {
      enqueueSnackbar("ℹ️ Item already added", { variant: "info" });
      return;
    }
    
    if (item.quantity <= 0) {
      enqueueSnackbar("⚠️ Item is out of stock", { variant: "warning" });
      return;
    }
    
    setSelectedItems([...selectedItems, { item, quantity: 1 }]);
  };

  const changeQuantity = (index, newQuantity) => {
    const updated = [...selectedItems];
    const maxQuantity = updated[index].item.quantity;
    
    if (newQuantity < 1) newQuantity = 1;
    if (newQuantity > maxQuantity) {
      newQuantity = maxQuantity;
      enqueueSnackbar(`⚠️ Maximum available: ${maxQuantity}`, { variant: "warning" });
    }
    
    updated[index].quantity = newQuantity;
    setSelectedItems(updated);
  };

  const removeItem = (id) => {
    setSelectedItems(selectedItems.filter((i) => i.item._id !== id));
  };

  const filteredItems = linenItems.filter((item) => 
    item.name.toLowerCase().includes(search.toLowerCase()) || 
    (item.code || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const validateStep1 = () => {
    if (!selectedWard) {
      enqueueSnackbar("❌ Please select a ward", { variant: "warning" });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (selectedItems.length === 0) {
      enqueueSnackbar("❌ Please add at least one linen item", { variant: "warning" });
      return false;
    }
    
    // Check if any item has invalid quantity
    const invalidItems = selectedItems.filter(item => item.quantity < 1 || item.quantity > item.item.quantity);
    if (invalidItems.length > 0) {
      enqueueSnackbar("❌ Some items have invalid quantities", { variant: "warning" });
      return false;
    }
    
    return true;
  };

  const nextStep = () => {
    if (activeStep === 0 && !validateStep1()) return;
    if (activeStep === 1 && !validateStep2()) return;
    setActiveStep(activeStep + 1);
  };

  const prevStep = () => {
    if (activeStep === 0) return;
    setActiveStep(activeStep - 1);
  };

  const resetForm = () => {
    setSelectedWard("");
    setSelectedRoom("");
    setSelectedItems([]);
    setNotes("");
    setActiveStep(0);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ward: selectedWard,
        room: selectedRoom,
        items: selectedItems.map((si) => ({
          linen: si.item._id,
          quantity: si.quantity
        })),
        notes: notes.trim() || undefined
      };

      const result = await createLinenIssue(payload);
      
      if (result.data.success) {
        enqueueSnackbar(
          `🎉 Successfully issued ${result.data.issued.length} item(s)`, 
          { variant: "success" }
        );
        
        if (result.data.warnings) {
          result.data.warnings.forEach(warning => {
            enqueueSnackbar(warning, { variant: "warning" });
          });
        }
        
        setOpenConfirm(false);
        resetForm();
      } else {
        throw new Error(result.data.message || "Issue failed");
      }
    } catch (err) {
      console.error("Submit error:", err);
      enqueueSnackbar(
        err.response?.data?.message || err.message || "❌ Issue failed", 
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  // Calculate totals
  const totalItems = selectedItems.length;
  const totalQuantity = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  const selectedWardName = wards.find(w => w._id === selectedWard)?.name || selectedWard;
  const selectedRoomInfo = rooms.find(r => r._id === selectedRoom);

  if (loading) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Linen Issue...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing wards and linen items
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Inventory sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Linen Issue
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Issue linen items to wards and rooms
              </Typography>
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton 
                  onClick={prevStep} 
                  disabled={activeStep === 0}
                  sx={{ 
                    backgroundColor: 'white',
                    color: 'primary.main',
                    '&:hover': { backgroundColor: 'grey.100' }
                  }}
                >
                  <ArrowBack sx={{ fontSize: 28 }} />
                </IconButton>
                
                {/* Progress Stats */}
                {activeStep > 0 && (
                  <Stack direction="row" spacing={2}>
                    <Chip 
                      icon={<LocalHospital sx={{ fontSize: 20 }} />} 
                      label={selectedWardName || "No Ward Selected"} 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                    />
                    {selectedRoomInfo && (
                      <Chip 
                        icon={<MeetingRoom sx={{ fontSize: 20 }} />} 
                        label={`Room: ${selectedRoomInfo.room_no}`} 
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                      />
                    )}
                    {totalItems > 0 && (
                      <Chip 
                        icon={<Inventory sx={{ fontSize: 20 }} />} 
                        label={`${totalItems} Items`} 
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel 
                  sx={{ 
                    '& .MuiStepLabel-label': { 
                      fontSize: '1.2rem', 
                      fontWeight: 600 
                    } 
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step 1: Location Selection */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalHospital sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Select Location
                    </Typography>
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              />
              <CardContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="medium">
                      <InputLabel sx={{ fontSize: '1.1rem' }}>Ward *</InputLabel>
                      <Select
                        value={selectedWard}
                        label="Ward *"
                        onChange={(e) => setSelectedWard(e.target.value)}
                        sx={{ fontSize: '1.1rem' }}
                        startAdornment={
                          <InputAdornment position="start">
                            <LocalHospital sx={{ color: 'primary.main', fontSize: 28 }} />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="" sx={{ fontSize: '1.1rem' }}>-- Select Ward --</MenuItem>
                        {wards.map(ward => (
                          <MenuItem key={ward._id} value={ward._id} sx={{ fontSize: '1.1rem' }}>
                            {ward.name}
                            {ward.price_category && (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '1rem' }}>
                                ({ward.price_category})
                              </Typography>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="medium" disabled={!selectedWard || loadingWards}>
                      <InputLabel sx={{ fontSize: '1.1rem' }}>Room</InputLabel>
                      <Select
                        value={selectedRoom}
                        label="Room"
                        onChange={(e) => setSelectedRoom(e.target.value)}
                        sx={{ fontSize: '1.1rem' }}
                        startAdornment={
                          <InputAdornment position="start">
                            <MeetingRoom sx={{ color: 'primary.main', fontSize: 28 }} />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="" sx={{ fontSize: '1.1rem' }}>-- Select Room (Optional) --</MenuItem>
                        {rooms.map(room => (
                          <MenuItem key={room._id} value={room._id} sx={{ fontSize: '1.1rem' }}>
                            {room.room_no}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '1rem' }}>
                              ({room.bed_count} beds, {room.occupied_beds} occupied)
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      {loadingWards && (
                        <CircularProgress size={24} sx={{ position: 'absolute', right: 40, top: '50%', transform: 'translateY(-50%)' }} />
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Ward Statistics */}
                {selectedWard && rooms.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 3 }}>
                      🏥 Ward Overview
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <StatsCard
                          title="Total Rooms"
                          value={rooms.length}
                          subtitle="Available rooms"
                          color="info"
                          icon={Hotel}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatsCard
                          title="Total Beds"
                          value={rooms.reduce((sum, room) => sum + room.bed_count, 0)}
                          subtitle="All rooms combined"
                          color="primary"
                          icon={Bed}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatsCard
                          title="Occupied Beds"
                          value={rooms.reduce((sum, room) => sum + room.occupied_beds, 0)}
                          subtitle="Currently occupied"
                          color="warning"
                          icon={TrendingUp}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                  <Button 
                    variant="outlined" 
                    onClick={prevStep} 
                    disabled
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Back
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={nextStep}
                    disabled={!selectedWard}
                    startIcon={<Inventory sx={{ fontSize: 28 }} />}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Select Linen Items
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 100
            }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    💡 Quick Tips
                  </Typography>
                }
              />
              <CardContent>
                <Alert severity="info" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                  Select the ward where linen will be issued. Room selection is optional but recommended for better tracking.
                </Alert>
                <Stack spacing={1}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • Required fields are marked with *
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • Room selection helps track linen usage per room
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • You can issue to ward without specifying a room
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2: Linen Selection */}
      {activeStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Inventory sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Select Linen Items
                    </Typography>
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              />
              <CardContent>
                <TextField
                  fullWidth
                  placeholder="Search by name, code, or category..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "primary.main", fontSize: 28 }} />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: '1.1rem',
                      '& input': {
                        padding: '12px 14px',
                        fontSize: '1.1rem'
                      }
                    }
                  }}
                  sx={{ mb: 3 }}
                />

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'primary.main' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📦 Item
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🗂️ Category
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📊 Available
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🛠️ Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredItems.map(item => (
                        <TableRow 
                          key={item._id} 
                          hover
                          sx={{ 
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              transform: 'translateY(-1px)',
                              transition: 'all 0.3s ease'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {item.code || "No Code"}
                            </Typography>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary" display="block" sx={{ fontSize: '1rem' }}>
                                {item.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={item.category || "Uncategorized"} 
                              size="medium" 
                              variant="outlined"
                              sx={{ fontSize: '1rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontSize: '1.1rem', 
                                fontWeight: 600,
                                color: item.quantity <= (item.min_stock || 0) ? "error" : "success.main"
                              }}
                            >
                              {item.quantity} {item.unit || "pcs"}
                            </Typography>
                            {item.quantity <= (item.min_stock || 0) && (
                              <Typography variant="body2" color="error" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                ⚠️ Low Stock
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Button 
                              variant="contained" 
                              size="medium"
                              onClick={() => addItem(item)}
                              disabled={item.quantity <= 0}
                              sx={{ 
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2
                              }}
                            >
                              Add to Issue
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              <Inventory sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                              <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                                No Items Found
                              </Typography>
                              <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                                {search ? "No items found matching your search" : "No items available"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 100
            }}>
              <CardHeader
                title={
                  <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    🛒 Selected Items ({selectedItems.length})
                  </Typography>
                }
              />
              <CardContent>
                <Divider sx={{ my: 2 }} />

                {selectedItems.length === 0 ? (
                  <Alert severity="info" sx={{ fontSize: '1.1rem', borderRadius: 2 }}>
                    No items selected
                  </Alert>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {selectedItems.map((selectedItem, index) => (
                      <Box 
                        key={selectedItem.item._id} 
                        sx={{ 
                          mb: 2, 
                          p: 2, 
                          border: '2px solid', 
                          borderColor: 'primary.main', 
                          borderRadius: 2,
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                              {selectedItem.item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                              Available: {selectedItem.item.quantity} {selectedItem.item.unit || "pcs"}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="large" 
                            color="error" 
                            onClick={() => removeItem(selectedItem.item._id)}
                            sx={{ 
                              backgroundColor: 'error.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'error.dark' }
                            }}
                          >
                            <Remove sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Quantity:</Typography>
                          <TextField
                            type="number"
                            size="medium"
                            value={selectedItem.quantity}
                            onChange={(e) => changeQuantity(index, parseInt(e.target.value) || 1)}
                            inputProps={{ 
                              min: 1, 
                              max: selectedItem.item.quantity,
                              style: { 
                                width: 100, 
                                textAlign: 'center',
                                fontSize: '1.1rem'
                              }
                            }}
                            variant="outlined"
                          />
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                            {selectedItem.item.unit || "pcs"}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {selectedItems.length > 0 && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 3, 
                    bgcolor: 'success.main', 
                    color: 'white', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" sx={{ fontSize: '1.3rem', fontWeight: 700, mb: 1 }}>
                      📊 Summary
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                      Total Items: {totalItems}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                      Total Quantity: {totalQuantity}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                  <Button 
                    variant="outlined" 
                    onClick={prevStep} 
                    fullWidth
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 3,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Back
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={nextStep}
                    disabled={selectedItems.length === 0}
                    fullWidth
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Review & Submit
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 3: Review & Submit */}
      {activeStep === 2 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title={
              <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                👁️ Review & Submit Issue
              </Typography>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                      📍 Location Details
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        <strong>🏢 Ward:</strong> {selectedWardName}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        <strong>🚪 Room:</strong> {selectedRoomInfo ? selectedRoomInfo.room_no : "Not specified"}
                      </Typography>
                      {selectedRoomInfo && (
                        <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                          {selectedRoomInfo.bed_count} beds, {selectedRoomInfo.occupied_beds} occupied
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                      📊 Issue Summary
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        <strong>📦 Total Items:</strong> {totalItems}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        <strong>🔢 Total Quantity:</strong> {totalQuantity}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'primary.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📦 Item Name
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🏷️ Code
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🗂️ Category
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🔢 Quantity
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📏 Unit
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItems.map(selectedItem => (
                    <TableRow 
                      key={selectedItem.item._id}
                      hover
                      sx={{ 
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          transform: 'translateY(-1px)',
                          transition: 'all 0.3s ease'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {selectedItem.item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {selectedItem.item.code || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {selectedItem.item.category || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {selectedItem.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {selectedItem.item.unit || "pcs"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              fullWidth
              label="📝 Notes (Optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 3 }}
              placeholder="Add any additional notes about this issue..."
              size="medium"
              InputProps={{
                sx: { fontSize: '1.1rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <Description sx={{ color: 'primary.main', fontSize: 28 }} />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
            />

            <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={prevStep}
                sx={{ 
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3
                }}
              >
                Back
              </Button>
              <Button 
                variant="contained" 
                onClick={() => setOpenConfirm(true)}
                startIcon={<CheckCircle sx={{ fontSize: 28 }} />}
                sx={{ 
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3
                }}
              >
                Submit Issue
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={openConfirm} 
        onClose={() => !saving && setOpenConfirm(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'warning.main', 
          color: 'white',
          py: 3
        }}>
          <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
            ⚠️ Confirm Linen Issue
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
            You are about to issue {totalQuantity} items across {totalItems} different linen types to {selectedWardName}.
            This action will reduce the available stock.
          </Alert>
          
          <Stack spacing={1}>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>🏢 Ward:</strong> {selectedWardName}
            </Typography>
            {selectedRoomInfo && (
              <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                <strong>🚪 Room:</strong> {selectedRoomInfo.room_no}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>📦 Total Items:</strong> {totalItems}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>🔢 Total Quantity:</strong> {totalQuantity}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setOpenConfirm(false)} 
            disabled={saving}
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle sx={{ fontSize: 24 }} />}
            sx={{ 
              fontSize: '1.2rem',
              fontWeight: 600,
              px: 4,
              py: 1,
              minWidth: 200
            }}
          >
            {saving ? "Processing..." : "Confirm Issue"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinenIssue;